/*

  Game.ts

  The Game class, as used in the single-page UI
  Now extends BaseGame and implements INetskraflGame

  Copyright (C) 2024 Miðeind ehf.
  Author: Vilhjalmur Thorsteinsson

  The Creative Commons Attribution-NonCommercial 4.0
  International Public License (CC-BY-NC 4.0) applies to this software.
  For further information, see https://github.com/mideind/Netskrafl

*/

import { m } from "./mithril";
import { request } from "./request";
import { wordChecker } from "./wordcheck";
import { GlobalState } from "./globalstate";
import { BaseGame, START_COORD, START_SQUARE } from "./basegame";
import {
  BOARD_SIZE,
  IGame, Message, Move, MoveDetail,
  MoveListener, RACK_SIZE, RackTile, ROWIDS,
  ServerGame, StatsType,
} from "./types";
import { coord, toVector } from "./util";

// Maximum overtime before a player loses the game, 10 minutes in seconds
export const MAX_OVERTIME = 10 * 60.0;
export const DEBUG_OVERTIME = 1 * 60.0;

const GAME_OVER = 99; // Error code corresponding to the Error class in skraflmechanics.py

export class Game extends BaseGame implements IGame {

  // A class that represents a Netskrafl game instance on the client

  // Netskrafl-specific properties
  userid: [string, string] = ["", ""];
  nickname: [string, string] = ["", ""];
  fullname: [string, string] = ["", ""];
  autoplayer: [boolean, boolean] = [false, false];
  maxOvertime: number = MAX_OVERTIME;

  // Netskrafl-specific game state
  scores: [number, number] = [0, 0];
  moves: Move[] = [];
  newmoves: Move[] = [];
  lastmove: MoveDetail[] | undefined = undefined;
  rack: RackTile[] = [];
  racks: string[] = []; // Racks at various points in the game, for game review
  num_moves: number = 0;
  bag = "";
  newbag: boolean = true;
  localturn: boolean = false;
  player: number | null = null; // null means that the current user is not a participant
  stats: StatsType | null | undefined = null; // Game review statistics
  moveListener: MoveListener;

  // Netskrafl-specific flags
  manual: boolean = false;
  fairplay: boolean = false;
  zombie: boolean = false; // !!! FIXME
  overdue: boolean = false; // > 14 days since last move without reply from opponent

  // Chat and messaging
  messages: Message[] | null = null; // Chat messages associated with this game
  chatLoading: boolean = false; // True while the chat messages are being loaded
  chatSeen: boolean = true; // False if the user has not seen all chat messages
  congratulate: boolean = false; // Show congratulation message if true
  
  // Game mechanics
  xchg: boolean = false; // Exchange allowed?
  chall: boolean = false; // Challenge allowed?
  last_chall: boolean = false; // True if last move laid down and asking for challenge
  succ_chall: boolean = false;
  moveInProgress: boolean = false; // Is the server processing a move?
  isFresh: boolean = false;
  numTileMoves: number = 0;

  // Timed game clock stuff
  interval: number | null = null; // Game clock interval timer
  time_info: { duration: number; elapsed: [number, number] } | null = null; // Information about elapsed time
  penalty0 = 0;
  penalty1 = 0;
  timeBase: Date | null = null; // Game time base
  runningOut0 = false;
  runningOut1 = false;
  blinking0 = false;
  blinking1 = false;
  clockText0 = "";
  clockText1 = "";

  constructor(
    uuid: string,
    srvGame: ServerGame | null,
    moveListener: MoveListener,
    state: GlobalState,
    maxOvertime?: number,
  ) {
    // Call parent constructor
    super(uuid, state); // Default board_type: "standard"
    this.moveListener = moveListener;

    if (maxOvertime !== undefined)
      // Maximum time override, for debugging purposes
      this.maxOvertime = maxOvertime;

    if (srvGame === null) {
      // Placeholder game for display purposes (e.g., Gáta Dagsins)
      this.initMockupGame();
      return;
    }

    // Load previously saved tile positions from local storage, if any
    let savedTiles = this.localStorage?.loadTiles() || [];
    this.init(srvGame);
    // Put tiles in the same position as they were when the player left the game
    this.restoreTiles(savedTiles);
    if (!this.over && this.isTimed())
      // Ongoing timed game: start the clock
      this.startClock();
    // Kick off loading of chat messages, if this is not a robot game
    if (!this.isRobotGame())
      this.loadMessages();
  }

  init(srvGame?: ServerGame) {
    if (!srvGame) {
      // Call parent init for base functionality
      super.init();
      return;
    }
    // Initialize the game state with data from the server
    // Check whether the game is over, or whether there was an error
    this.over = srvGame.result === GAME_OVER;
    if (this.over || srvGame.result === 0) {
      this.currentError = this.currentMessage = null;
    } else {
      // Nonzero srvGame.result: something is wrong
      this.currentError = srvGame.result || "server";
      this.currentMessage = srvGame.msg || "";
      return;
    }
    // Copy srvGame JSON properties over to this object
    Object.assign(this, srvGame);
    if (srvGame.newmoves) {
      // Add the newmoves list, if any, to the list of moves
      this.moves = this.moves.concat(srvGame.newmoves);
    }
    // Don't keep the new moves lying around
    this.newmoves = [];
    this.localturn = !this.over && ((this.moves.length % 2) === this.player);
    this.isFresh = true;
    this.startSquare = START_SQUARE[this.board_type];
    this.startCoord = START_COORD[this.board_type];
    // If the game is over and this player has more points than
    // the opponent, congratulations are in order
    this.congratulate = this.over && this.player !== null &&
      (this.scores[this.player] > this.scores[1 - this.player]);
    if (this.currentError === null)
      // Generate a dictionary of tiles currently on the board,
      // from the moves already made. Also highlights the most recent
      // opponent move (contained in this.lastmove)
      this.placeTiles();
    // Initialize the word cache with two-letter words.
    // Note that this.two_letter_words contains two lists, ordered
    // by the first letter and by the second letter. We only need
    // to process one of them.
    wordChecker.ingestTwoLetterWords(this.locale, this.two_letter_words[0]);
  };

  initMockupGame() {
    // Initialize a mockup game for testing purposes (e.g., Gáta Dagsins)
    this.tiles = {}; // Empty board - this is the key property needed for Board component
    this.startSquare = START_SQUARE[this.board_type];
    this.startCoord = START_COORD[this.board_type];
    // Fill in a few standard pieces of data to work with during testing
    this.alphabet = "aábdðeéfghiíjklmnoóprstuúvxyýþæö";
    // Icelandic tile set
    this.localturn = true;
    this.player = 0;
    this.tile_scores = {
      "?":0,"a":1,"b":5,"d":5,"e":3,"f":3,"g":3,
      "h":4,"i":1,"j":6,"k":2,"l":2,"m":2,"n":1,
      "o":5,"p":5,"r":1,"s":1,"t":2,"u":2,"v":5,
      "x":10,"y":6,"á":3,"æ":4,"é":7,"í":4,"ð":2,
      "ó":3,"ö":6,"ú":4,"ý":5,"þ":7,
    };
    this.two_letter_words = [
      [
        ["a", ["að","af","ak","al","an","ar","as","at","ax"]],
        ["á",["áa","áð","ái","ál","ám","án","ár","ás","át"]],
        ["b",["bí","bú","bý","bæ"]],
        ["d",["dá","do","dó","dý"]],
        ["e",["eð","ef","eg","ei","ek","el","em","en","er","es","et","ex","ey"]],
        ["é",["ég","él","ét"]],
        ["f",["fa","fá","fé","fæ"]],
        ["g",["gá"]],
        ["h",["ha","há","hí","hó","hý","hæ"]],
        ["i",["ið","il","im"]],
        ["í",["íð","íl","ím","ís"]],
        ["j",["já","je","jó","jú"]],
        ["k",["ká","ku","kú"]],
        ["l",["la","lá","lé","ló","lú","lý","læ"]],
        ["m",["má","mi","mó","mý"]],
        ["n",["ná","né","nó","nú","ný","næ"]],
        ["o",["of","og","oj","ok","op","or"]],
        ["ó",["óa","óð","óf","ói","ók","ól","óm","ón","óp","ós","óx"]],
        ["p",["pí","pu","pú","pæ"]],
        ["r",["rá","re","ré","rí","ró","rú","rý","ræ"]],
        ["s",["sá","sé","sí","so","sú","sý","sæ"]],
        ["t",["tá","te","té","ti","tí","tó","tý"]],
        ["u",["um","un"]],
        ["ú",["úa","úð","úf","úi","úr","út"]],
        ["v",["vá","vé","ví","vó"]],
        ["y",["yl","ym","yr","ys"]],
        ["ý",["ýf","ýg","ýi","ýk","ýl","ýr","ýs","ýt"]],
        ["þ",["þá","þó","þú","þý"]],
        ["æ",["æð","æf","æg","æi","æl","æp","ær","æs","æt"]],
        ["ö",["öl","ör","ös","öt","öx"]],
      ],
      [
        ["a",["áa","fa","ha","la","óa","úa"]],
        ["á",["dá","fá","gá","há","já","ká","lá","má","ná","rá","sá","tá","vá","þá"]],
        ["ð",["að","áð","eð","ið","íð","óð","úð","æð"]],
        ["e",["je","re","te"]],
        ["é",["fé","lé","né","ré","sé","té","vé"]],
        ["f",["af","ef","of","óf","úf","ýf","æf"]],
        ["g",["eg","ég","og","ýg","æg"]],
        ["i",["ái","ei","mi","ói","ti","úi","ýi","æi"]],
        ["í",["bí","hí","pí","rí","sí","tí","ví"]],
        ["j",["oj"]],["k",["ak","ek","ok","ók","ýk"]],
        ["l",["al","ál","el","él","il","íl","ól","yl","ýl","æl","öl"]],
        ["m",["ám","em","im","ím","óm","um","ym"]],
        ["n",["an","án","en","ón","un"]],
        ["o",["do","so"]],
        ["ó",["dó","hó","jó","ló","mó","nó","ró","tó","vó","þó"]],
        ["p",["op","óp","æp"]],
        ["r",["ar","ár","er","or","úr","yr","ýr","ær","ör"]],
        ["s",["as","ás","es","ís","ós","ys","ýs","æs","ös"]],
        ["t",["at","át","et","ét","út","ýt","æt","öt"]],
        ["u",["ku","pu"]],
        ["ú",["bú","jú","kú","lú","nú","pú","rú","sú","þú"]],
        ["x",["ax","ex","óx","öx"]],
        ["y",["ey"]],
        ["ý",["bý","dý","hý","lý","mý","ný","rý","sý","tý","þý"]],
        ["æ",["bæ","fæ","hæ","læ","næ","pæ","ræ","sæ"]]
      ]
    ];
    // Initialize rack with TESTING tiles for demonstration
    const rackTiles: RackTile[] = [
      ["t", 1],
      ["e", 1], 
      ["s", 1],
      ["t", 1],
      ["i", 1],
      ["n", 1],
      ["g", 2],
    ];

    this.rack = rackTiles;

    // Add rack tiles to game.tiles dictionary with R1-R7 coordinates
    for (let i = 0; i < rackTiles.length; i++) {
      const [tile, score] = rackTiles[i];
      this.tiles[`R${i + 1}`] = {
        player: 0,
        tile: tile,
        letter: tile,
        score: score,
        freshtile: true,
        draggable: true,
        index: i,
        xchg: false
      };
    }
    wordChecker.ingestTwoLetterWords(this.locale, this.two_letter_words[0]);
  }

  update(srvGame: ServerGame) {
    // Update the srvGame state with data from the server,
    // either after submitting a move to the server or
    // after receiving a move notification via the Firebase listener
    // Remember if the game was already won before this update
    const wasWon = this.congratulate;
    // Stop highlighting the previous opponent move, if any
    for (let sq in this.tiles)
      if (this.tiles.hasOwnProperty(sq))
        this.tiles[sq].freshtile = false;
    this.init(srvGame);
    if (this.currentError === null) {
      if (this.succ_chall) {
        // Successful challenge: reset the rack
        // (this updates the score as well)
        this.resetRack();
      }
      else {
        this.updateScore();
      }
    }
    this.saveTiles();
    if (this.isTimed())
      // The call to resetClock() clears any outstanding interval timers
      // if the srvGame is now over
      this.resetClock();
    // Notify the move listener if the game just transitioned to won
    if (!wasWon && this.congratulate && this.moveListener) {
      this.moveListener.notifyGameWon();
    }
  };

  async refresh() {
    // Force a refresh of the current game state from the server
    // Before calling refresh(), this.moveInProgress is typically
    // set to true, so we reset it here
    try {
      if (!this.uuid)
        return;
      const result = await request<{ ok: boolean; game: ServerGame; }>(this.state, {
        method: "POST",
        url: "/gamestate",
        body: { game: this.uuid }  // !!! FIXME: Add delete_zombie parameter
      });
      if (!result?.ok) {
        // console.log("Game " + uuid + " could not be loaded");
      }
      else {
        this.update(result.game);
      }
    }
    catch(e) {
    }
    finally {
      this.moveInProgress = false;
    }
  }

  notifyUserChange(newNick: string) {
    // The user information may have been changed:
    // perform any updates that may be necessary
    if (this.player !== null)
      // The player nickname may have been changed
      this.nickname[this.player] = newNick;
  };

  buttonState(): Record<string, boolean> {
    // Calculate a set of booleans describing the state of the game
    let s: Record<string, boolean> = {};
    s.tilesPlaced = this.tilesPlaced().length > 0;
    s.gameOver = this.over;
    s.congratulate = this.congratulate;
    s.localTurn = this.localturn;
    s.gameIsManual = this.manual;
    s.challengeAllowed = this.chall;
    s.lastChallenge = this.last_chall;
    s.showingDialog = this.showingDialog !== null;
    s.exchangeAllowed = this.xchg;
    s.wordGood = this.wordGood;
    s.wordBad = this.wordBad;
    s.canPlay = false;
    s.tardyOpponent = !s.localTurn && !s.gameOver && this.overdue;
    s.showResign = false;
    s.showExchange = false;
    s.showPass = false;
    s.showRecall = false;
    s.showScramble = false;
    s.showMove = false;
    s.showMoveMobile = false; // Versatile move button for mobile UI
    s.showForceResignMobile = false; // Force resignation button for mobile UI
    s.showChallenge = false;
    s.showChallengeInfo = false;
    if (this.moveInProgress)
      // While a move is in progress (en route to the server)
      // no buttons are shown
      return s;
    if (s.localTurn && !s.gameOver) {
      // This player's turn
      if (s.lastChallenge) {
        s.showChallenge = true;
        s.showPass = true;
        s.showChallengeInfo = true;
      }
      else {
        s.showMove = s.tilesPlaced;
        s.showExchange = !s.tilesPlaced;
        s.showPass = !s.tilesPlaced;
        s.showResign = !s.tilesPlaced;
        s.showChallenge = !s.tilesPlaced && s.gameIsManual && s.challengeAllowed;
      }
    }
    if (s.showMove && (s.wordGood || s.gameIsManual))
      s.canPlay = true;
    if (!s.gameOver)
      if (s.tilesPlaced) {
        s.showRecall = true;
        s.showMoveMobile = true;
      } else {
        s.showScramble = true;
        if (s.tardyOpponent)
          // Not showing the move button: show the Force resignation button
          s.showForceResignMobile = true;
      }
    return s;
  }

  isTimed(): boolean {
    // Return True if this is a timed game
    return this.time_info !== null && this.time_info.duration >= 1.0;
  };

  showClock(): boolean {
    // Return true if the clock should be shown in the right-hand column
    if (!this.isTimed())
      // Only show the clock for a timed game, obviously
      return false;
    if (!this.over)
      // If the game is still ongoing, always show the clock
      return true;
    // If the game is over, only show the clock if there is something to
    // show, i.e. at least one clock text
    return !!this.clockText0 || !!this.clockText1;
  }

  updateClock() {
    var txt0 = this.calcTimeToGo(0);
    var txt1 = this.calcTimeToGo(1);
    this.clockText0 = txt0;
    this.clockText1 = txt1;
    // If less than two minutes left, indicate that time is running out
    this.runningOut0 = (txt0[0] == "-" || txt0 <= "02:00");
    this.runningOut1 = (txt1[0] == "-" || txt1 <= "02:00");
    // If less than 30 seconds left, make the clock digits blink
    this.blinking0 = (this.runningOut0 && txt0 >= "00:00" && txt0 <= "00:30" && this.player === 0);
    this.blinking1 = (this.runningOut1 && txt1 >= "00:00" && txt1 <= "00:30" && this.player === 1);
    m.redraw();
  }

  resetClock() {
    // Set a new time base after receiving an update from the server
    this.timeBase = new Date();
    this.updateClock();
    if (this.over) {
      // Game over: reset stuff
      if (this.interval) {
        window.clearInterval(this.interval);
        this.interval = null;
      }
      this.blinking0 = false;
      this.blinking1 = false;
      this.runningOut0 = false;
      this.runningOut1 = false;
    }
  }

  startClock() {
    // Start the clock running, after loading a timed game
    this.resetClock();
    if (!this.interval) {
      this.interval = window.setInterval(
        () => { this.updateClock(); },
        500 // milliseconds, i.e. 0.5 seconds
      );
    }
  }

  cleanup() {
    // Clean up any resources owned by this game object
    if (this.interval) {
      window.clearInterval(this.interval);
      this.interval = null;
    }
  }

  calcTimeToGo(player: 0 | 1): string {
    /* Return the time left for a player in a nice MM:SS format */
    let gameTime = this.time_info;
    if (gameTime === null || this.timeBase === null) return ""; // Should not happen
    let elapsed = gameTime.elapsed[player];
    let gameOver = this.over;
    if (!gameOver && (this.moves.length % 2) === player) {
      // This player's turn: add the local elapsed time
      let now = new Date();
      elapsed += (now.getTime() - this.timeBase.getTime()) / 1000;
      if (elapsed - gameTime.duration * 60.0 > this.maxOvertime) {
        // 10 minutes overtime has passed: The client now believes
        // that the player has lost. Refresh the game from the server
        // to get its final verdict.
        if (!this.moveInProgress) {
          this.moveInProgress = true;
          // Refresh from the server in half a sec, to be a little
          // more confident that it agrees with us
          window.setTimeout(
            () => { this.refresh(); }, 500
          );
        }
      }
    }
    // The overtime is max 10 minutes - at that point you lose
    const timeToGo = Math.max(gameTime.duration * 60.0 - elapsed, -this.maxOvertime);
    const absTime = Math.abs(timeToGo);
    const min = Math.floor(absTime / 60.0);
    const sec = Math.floor(absTime - min * 60.0);
    if (gameOver) {
      // We already got a correct score from the server
      this.penalty0 = 0;
      this.penalty1 = 0;
    }
    else
    if (timeToGo < 0.0) {
      // We're into overtime: calculate the score penalty
      if (player === 0)
        this.penalty0 = -10 * Math.floor((min * 60 + sec + 59) / 60);
      else
        this.penalty1 = -10 * Math.floor((min * 60 + sec + 59) / 60);
    }
    return (timeToGo < 0.0 ? "-" : "") +
       ("0" + min.toString()).slice(-2) + ":" + ("0" + sec.toString()).slice(-2);
  }

  displayScore(player: 0 | 1): number {
    // Return the score to be displayed, which is the current
    // actual game score minus accrued time penalty, if any, in a timed game
    return Math.max(
      this.scores[player] + (player === 0 ? this.penalty0 : this.penalty1), 0
    )
  }

  isRobotGame(): boolean {
    // Return true if any player in the game is a robot
    return this.autoplayer[0] || this.autoplayer[1];
  }

  async loadMessages() {
    // Load chat messages for this game
    if (this.chatLoading)
      // Already loading
      return;
    this.chatLoading = true;
    this.messages = [];
    try {
      const result = await request<{ ok: boolean; messages: Message[]; seen?: boolean }>(this.state,
        {
          method: "POST",
          url: "/chatload",
          body: { channel: "game:" + this.uuid }
        }
      );
      if (result.ok)
        this.messages = result.messages || [];
      else
        this.messages = [];
      // Note whether the user has seen all chat messages
      if (result.seen === undefined)
        this.chatSeen = true;
      else
        this.chatSeen = result.seen;
    }
    catch (e) {
      // Just leave this.messages as an empty list
    }
    finally {
      this.chatLoading = false;
    }
  }

  async loadStats() {
    // Load statistics about a game
    this.stats = undefined; // Error/in-progress status
    try {
      const json = await request<{ result?: number }>(this.state,
        {
          method: "POST",
          url: "/gamestats",
          body: { game: this.uuid }
        }
      );
      // Save the incoming game statistics in the stats property
      if (!json || json.result === undefined)
        return;
      if (json.result !== 0 && json.result !== GAME_OVER)
        return;
      // Success: assign the stats
      this.stats = json as StatsType;
    }
    catch(e) {
      // Just leave this.stats undefined
    }
  }

  async sendMessage(msg: string) {
    // Send a chat message
    try {
      await request(this.state,
        {
          method: "POST",
          url: "/chatmsg",
          body: { channel: "game:" + this.uuid, msg: msg }
        }
      );
    }
    catch(e) {
      // No big deal
      // A TODO might be to add some kind of error icon to the UI
    }
  }

  sendChatSeenMarker() {
    // Send a 'chat message seen' marker to the server
    this.sendMessage("");
    // The user has now seen all chat messages
    this.chatSeen = true;
  }

  addChatMessage(from_userid: string, msg: string, ts: string, ownMessage: boolean) {
    // Add a new chat message, received via a Firebase notification,
    // to the message list
    if (this.chatLoading || msg === "")
      // Loading of the message list is underway: assume that this message
      // will be contained in the list, once it has been read
      return;
    if (this.messages === null) this.messages = [];
    this.messages.push({ from_userid: from_userid, msg: msg, ts: ts });
    if (this.sel == "chat") {
      // Chat already open, so the player has seen the message: send a read receipt
      this.sendChatSeenMarker();
    } else if (!ownMessage) {
      // Chat not open, and we have a new chat message from the other player:
      // note that this player hasn't seen it
      this.chatSeen = false;
    }
  }

  markChatShown(): boolean {
    // Note that the user has seen all pending chat messages
    if (!this.chatSeen) {
      this.sendChatSeenMarker();
      return true;
    }
    return false;
  }

  placeMove(player: 0 | 1, co: string, tiles: string, highlight: boolean) {
    // Place an entire move into the tiles dictionary
    const vec = toVector(co);
    let col = vec.col;
    let row = vec.row;
    let nextBlank = false;
    let index = 0;
    for (let i = 0; i < tiles.length; i++) {
      let tile = tiles[i];
      if (tile == '?') {
        nextBlank = true;
        continue;
      }
      const sq = coord(row, col);
      if (sq === null) continue; // Should not happen
      let letter = tile;
      if (nextBlank)
        tile = '?';
      const tscore = this.tilescore(tile);
      // Place the tile, if it isn't there already
      if (!(sq in this.tiles)) {
        this.tiles[sq] = {
          player,
          tile,
          letter,
          score: tscore,
          draggable: false,
          freshtile: false,
          index, // Index of this tile within the move, for animation purposes
          xchg: false,
        };
        if (highlight) {
          // Highlight the tile
          if (player === this.player)
            this.tiles[sq].highlight = 0; // Local player color
          else if ((1 - player) === this.player)
            this.tiles[sq].highlight = 1; // Remote player color
          index++;
        }
      }
      col += vec.dx;
      row += vec.dy;
      nextBlank = false;
    }
  }

  setRack(rack: RackTile[]) {
    // Set the current rack
    this.rack = rack;
  }

  rackAtMove(moveIndex: number): string {
    const numRacks = this.racks?.length || 0;
    if (!numRacks) return "";
    return this.racks[moveIndex >= numRacks ? numRacks - 1 : moveIndex];
  }

  isFinalMove(moveIndex: number): boolean {
    // Return true if the indicated move is a final (adjustment) move
    // in the game, i.e. a rack leave or a game 'OVER' move
    return moveIndex >= this.num_moves;
  }

  boardAsStrings(): string[] {
    // Return an array of strings, each representing a row of the board
    const board = [];
    for (const row of ROWIDS) {
      let line = "";
      for (let col = 0; col < BOARD_SIZE; col++) {
        let sq = `${row}${col + 1}`;
        if (sq in this.tiles) {
          const {tile, letter} = this.tiles[sq];
          // We indicate a blank tile meaning with an uppercase letter
          line += tile === "?" ? letter.toLocaleUpperCase() : letter;
        } else
          line += ".";
      }
      board.push(line);
    }
    return board;
  }

  placeTiles(move?: number, noHighlight?: boolean) {
    // Make a tile dictionary for the game.
    // If move is given, it is an index of the
    // last move in the move list that should be
    // shown on the board.
    this.tiles = {};
    this.numTileMoves = 0;
    const mlist = this.moves;
    // We highlight the last move placed (a) if we're in a game
    // review (move !== undefined) or (b) if this is a normal game
    // view, we don't have an explicit this.lastmove (which is treated
    // separately) and the last move is an opponent move.
    const highlightReview = (move !== undefined);
    const highlightLast = !highlightReview && !this.lastmove && this.localturn;
    const highlight = !noHighlight && (highlightLast || highlightReview);
    const last: number = (move !== undefined) ? move : mlist.length;

    function successfullyChallenged(ix: number): boolean {
      // Was the move with index ix successfully challenged?
      if (ix + 2 >= last)
        // The move list is too short for a response move
        return false;
      let [ _, [ co, tiles, score ] ] = mlist[ix + 2];
      if (co !== "")
        // The player's next move is a normal tile move
        return false;
      // Return true if this was a challenge response with a negative score
      // (i.e. a successful challenge)
      return (tiles === "RESP") && (score < 0);
    }

    // Loop through the move list, placing each move
    for (let i = 0; i < last; i++) {
      let [ player, [co, tiles] ] = mlist[i];
      if (co != "" && !successfullyChallenged(i)) {
        // Unchallenged tile move: place it on the board
        this.placeMove(player, co, tiles, (i === last - 1) && highlight);
        this.numTileMoves++;
      }
    }
    // If it's our turn, mark the opponent's last move
    // The type of this.lastmove corresponds to DetailTuple on the server side
    let dlist = this.lastmove;
    if (dlist && this.localturn)
      for (let i = 0; i < dlist.length; i++) {
        let sq = dlist[i][0];
        if (!(sq in this.tiles))
          throw "Tile from lastmove not in square " + sq;
        this.tiles[sq].freshtile = true;
        this.tiles[sq].index = i; // Index of tile within move, for animation purposes
      }
    // Also put the rack tiles into this.tiles
    for (let i = 0; i < this.rack.length; i++) {
      const sq = 'R' + (i + 1);
      const [tile, tscore] = this.rack[i];
      const letter = (tile === '?') ? ' ' : tile;
      this.tiles[sq] = {
        player: this.player ? 1 : 0,
        tile,
        letter,
        score: tscore,
        draggable: true,
        freshtile: false,
        index: 0,
        xchg: false
      };
    }
  };

  allowDragDrop(): boolean {
    // Return true if the game allows drag-and-drop of tiles
    return !this.over && !this.showingDialog && !this.moveInProgress;
  };

  async sendMove(moves: string[]) {
    // Send a move to the server
    this.moveInProgress = true;
    try {
      const result = await request<ServerGame>(this.state, {
        method: "POST",
        url: "/submitmove",
        body: { moves: moves, mcount: this.moves.length, uuid: this.uuid },
      });
      // The update() function also handles error results
      this.update(result);
      // Notify eventual listeners that a (local) move has been made
      if (this.moveListener)
        this.moveListener.notifyMove();
    } catch (e) {
      this.currentError = "server";
      if (e instanceof Error) {
        this.currentMessage = e.message;
      } else {
        this.currentMessage = String(e);
      }
    }
    finally {
      this.moveInProgress = false;
    }
  };

  async forceResign() {
    // Force resignation by a tardy opponent
    this.moveInProgress = true;
    try {
      const result = await request<ServerGame>(this.state,
        {
          method: "POST",
          url: "/forceresign",
          body: { mcount: this.moves.length, game: this.uuid }
        }
      );
      // The update() function also handles error results
      this.update(result);
    } catch (e) {
      this.currentError = "server";
      if (e instanceof Error) {
        this.currentMessage = e.message;
      } else {
        this.currentMessage = String(e);
      }
    }
    finally {
      this.moveInProgress = false;
    }
  };

  submitMove() {
    // Send a tile move to the server
    const t = this.tilesPlaced();
    let moves: string[] = [];
    this.selectedSq = null; // Currently selected (blinking) square
    for (let i = 0; i < t.length; i++) {
      const sq = t[i];
      const { tile, letter } = this.tiles[sq];
      moves.push(sq + "=" + tile + (tile === '?' ? letter : ""));
    }
    if (moves.length > 0)
      this.sendMove(moves);
  };

  submitPass() {
    // Show a pass confirmation prompt
    this.showingDialog = "pass";
    this.selectedSq = null; // Currently selected (blinking) square
  };

  submitChallenge() {
    // Show a challenge confirmation prompt
    this.showingDialog = "chall";
    this.selectedSq = null; // Currently selected (blinking) square
  };

  submitExchange() {
    // Show an exchange prompt
    this.showingDialog = "exchange";
    this.selectedSq = null; // Currently selected (blinking) square
    // Remove the xchg flag from all tiles in the rack
    for (let i = 1; i <= RACK_SIZE; i++) {
      const sq = "R" + i;
      if (sq in this.tiles)
        this.tiles[sq].xchg = false;
    }
  };

  submitResign() {
    // Show a resign prompt
    this.showingDialog = "resign";
    this.selectedSq = null; // Currently selected (blinking) square
  };

  confirmPass(yes: boolean) {
    // Handle reply to pass confirmation prompt
    this.showingDialog = null;
    if (yes)
      this.sendMove([ "pass" ]);
  };

  confirmChallenge(yes: boolean) {
    // Handle reply to challenge confirmation prompt
    this.showingDialog = null;
    if (yes)
      this.sendMove([ "chall" ]);
  };

  confirmExchange(yes: boolean) {
    // Handle reply to exchange confirmation prompt
    let exch = "";
    this.showingDialog = null;
    for (let i = 1; i <= RACK_SIZE; i++) {
      const sq = "R" + i;
      if (sq in this.tiles && this.tiles[sq].xchg) {
        // This tile is marked for exchange
        exch += this.tiles[sq].tile;
        this.tiles[sq].xchg = false;
      }
    }
    if (yes && exch.length > 0)
      // Send the exchange move to the server
      this.sendMove([ "exch=" + exch ]);
  };

  confirmResign(yes: boolean) {
    // Handle reply to resignation confirmation prompt
    this.showingDialog = null;
    if (yes)
      this.sendMove([ "rsgn" ]);
  };

} // class Game
